import { Api } from "../../Api";
import { DataFrame, Method, Status } from "../../DataFrame";
import { ExalusConnectionService } from "../ExalusConnectionService";
import { FieldChangeResult, FieldChangeResultType, ResponseResult } from "../FieldChangeResult";
import { IExalusConnectionService } from "../IExalusConnectionService";
import { ISessionService } from "../Session/ISessionService";
import { SessionService } from "../Session/SessionService";
import { IWebApiCacheService } from "../WebApi/IWebApiCacheService";
import { WebApiCacheService } from "../WebApi/WebApiCacheService";
import { DeviceChannel } from "./DeviceChannel";
import { AddNewGroupErrorCode, ChannelsGroup, IChannelsGroup, IChannelsGroupsService, SetObjectsInGroupAndOrderResponseCode } from "./IChannelsGroupsService";
import { IDeviceChannel } from "./IDeviceChannel";
import { ILoggerService } from '../Logging/ILoggerService';
import { LoggerService } from "../Logging/LoggerService";
import { DevicesService } from './DevicesService';
import { IDevicesService } from './IDevicesService';
import { ControllerConfigurationService } from "../Controller/ControllerConfigurationService";
import { IControllerConfigurationService } from "../Controller/IControllerConfigurationService";
import { IDevice } from "./IDevice";
import { DependencyContainer } from "../../DependencyContainer";
import { Event } from '../../Event';
import { AsyncSemaphore } from "../../AsyncSemaphore";

export class ChannelsGroupsService implements IChannelsGroupsService {
    public static readonly ServiceName: string = "ChannelsGroupsService";
    private _connection: IExalusConnectionService | null = null;
    private _synchronized: boolean = false;
    private _onSynchronized: Event = new Event();
    private _groups: IChannelsGroup[] = [];
    private _objectsGroupingSupported = false;
    private _configurationService: IControllerConfigurationService = Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName);
    private _syncSemaphore: AsyncSemaphore = new AsyncSemaphore(1);

    public constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
        Api.Get<ISessionService>(SessionService.ServiceName).OnUserLoggedInEvent().Subscribe(async user => {
            await this.ResyncGroupsAsync();
        });
    }

    private async ResyncGroupsAsync(): Promise<void> {
        const lock = await this._syncSemaphore.AcquireAsync();
        try {
            this._synchronized = false;

            await this.GetGroupsAsync(true);

            this._synchronized = true;
            this._onSynchronized.Invoke();
        } catch (ex) {
            DependencyContainer.Log?.Error(
                ChannelsGroupsService.ServiceName,
                `Failed to sync groups: ${ex}`
            );
        } finally {
            lock.Release();
        }
    }

    WaitForSynchronizationAsync(): Promise<void> {
        if (this._synchronized) return Promise.resolve();

        return new Promise<void>((resolve) => {
            const timeoutId = window.setTimeout(() => {
                this._onSynchronized.Unsubscribe(handler);
                resolve();
            }, 30_000);

            const handler = () => {
                window.clearTimeout(timeoutId);
                this._onSynchronized.Unsubscribe(handler);
                resolve();
            };

            this._onSynchronized.Subscribe(handler);
        });
    }


    async SetObjectsInGroupAndOrderAsync(group: IChannelsGroup, objects: (IDeviceChannel | IDevice)[]): Promise<ResponseResult<SetObjectsInGroupAndOrderResponseCode>> {
        let channelsIds: string[] = [];
        let objectsIds: string[] = [];

        for (let obj of objects) {
            if (this.IsDevice(obj)) {
                for (let channel of (obj as IDevice).Channels) {
                    channelsIds.push(`${(channel as IDeviceChannel).ChannelId}`);
                }
                objectsIds.push(`${(obj as IDevice).Guid}`);
            }
            else
                if (this.IsDeviceChannel(obj)) {
                    channelsIds.push(`${(obj as IDeviceChannel).ChannelId}`);
                    objectsIds.push(`${(obj as IDeviceChannel).ChannelId}`);
                }
                else
                    throw new Error("Passed object is not a device or device channel!");
        }

        const result = await this._connection?.SendAndWaitForResponseAsync<ApiGroupObject[]>(new SetObjectsInGroupAndOrderRequest(group, objectsIds), 15000, false);
        DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! SetObjectsInGroupAndOrderAsync()`);
        this._configurationService.ExitConfigurationModeAsync();
        await this.GetGroupsAsync(false);
        await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync();

        switch (result?.Status) {
            case Status.OK:
                return new ResponseResult(SetObjectsInGroupAndOrderResponseCode.Success, "");

            case Status.NotSupportedMethod:
            case Status.ResourceDoesNotExists:
            case Status.ResourceIsNotAvailable:
                return new ResponseResult(SetObjectsInGroupAndOrderResponseCode.FunctionNotSupported, "");

            case Status.WrongData:
                switch (result.Data as any as string) {
                    case "GroupDoesNotExists":
                        return new ResponseResult(SetObjectsInGroupAndOrderResponseCode.GroupDoesNotExists, result.Data as any as string);
                }

                return new ResponseResult(SetObjectsInGroupAndOrderResponseCode.FatalError, result.Data as any as string);

            case Status.Error:
            case Status.FatalError:
            default:
                return new ResponseResult(SetObjectsInGroupAndOrderResponseCode.FatalError, "");
        }

    }

    private IsDevice(obj: any): obj is IDevice {
        return (obj as IDevice).Channels !== undefined;
    }

    private IsDeviceChannel(obj: any): obj is IDeviceChannel {
        return (obj as IDeviceChannel).GetDevice !== undefined;
    }

    private LogGroupsOrder(orderedGroups: IChannelsGroup[]): void {
        const log = Api.Get<ILoggerService>(LoggerService.ServiceName);

        orderedGroups.forEach((group, index) => {
            log.Debug(ChannelsGroupsService.ServiceName, `Group ${group.Name} order position [on array]: ${index} set position: ${group.OrderPosition}`);
        });
    }

    private LogObjectsInGroupOrder(group: IChannelsGroup, channels: IDeviceChannel[]): void {
        const log = Api.Get<ILoggerService>(LoggerService.ServiceName);

        log.Debug(ChannelsGroupsService.ServiceName, `Group ${group.Name} objects order:`);

        channels.forEach((channel, index) => {
            log.Debug(ChannelsGroupsService.ServiceName, `Channel ${channel.Name} order position [on array]: ${index} channelId: ${channel.ChannelId}`);
        });

        for (const [key, value] of Object.entries(group.ObjectsOrder)) {
            log.Debug(`new order [GROUP ${group.Name}] Order: [${key}] = ${value}`);
        }
    }

    async ChangeGroupsOrderAsync(orderedGroups: IChannelsGroup[]): Promise<Status> {
        const orderedObjectsGuid = orderedGroups.select(a => a.Guid).toArray();
        const result = await this._connection?.SendAndWaitForResponseAsync<ApiGroupObject[]>(new ChangeGroupsOrderRequest(orderedObjectsGuid), 15000, false);
        DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! ChangeGroupsOrderAsync()`);
        this._configurationService.ExitConfigurationModeAsync();

        switch (result?.Status) {
            case Status.OK:
                await this.GetGroupsAsync(false);
                return Status.OK;

            default:
                Api.Get<ILoggerService>(LoggerService.ServiceName).Error(ChannelsGroupsService.ServiceName, `Failed to change groups order, controller responded with error ${result?.Status} data: ${result?.Data}`);
                this.LogGroupsOrder(orderedGroups);
                return result?.Status as Status;
        }
    }

    async ChangeObjectsPositionsInGroupAsync(group: IChannelsGroup, channels: IDeviceChannel[]): Promise<Status> {
        const objs = channels.select(a => a.ChannelId).toArray();
        const result = await this._connection?.SendAndWaitForResponseAsync<ApiGroupObject[]>(new MoveObjectsInGroupRequest(group.Guid, objs), 15000, false);
        DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! ChangeObjectsPositionsInGroupAsync()`);
        this._configurationService.ExitConfigurationModeAsync();

        switch (result?.Status) {
            case Status.OK:
                await this.GetGroupsAsync(false);
                await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync();
                return Status.OK;

            default:
                DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `Failed to change objects positions in group, controller responded with error ${result?.Status} data: ${result?.Data}`);
                this.LogObjectsInGroupOrder(group, channels);
                return result?.Status as Status;
        }
    }

    IsGroupsAndObjectsOrderingAvailable(): boolean {
        return this._objectsGroupingSupported;
        //return !this._groups.any(a => a.ObjectsOrder === undefined || a.ObjectsOrder === null);
    }

    async MoveGroupPositionAsync(group: IChannelsGroup, newPosition: number): Promise<Status> {
        const result = await this._connection?.SendAndWaitForResponseAsync<ApiGroupObject[]>(new MoveGroupRequest(group.Guid, newPosition), 15000, false);
        DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! MoveGroupPositionAsync()`);
        this._configurationService.ExitConfigurationModeAsync();

        switch (result?.Status) {
            case Status.OK:
                await this.GetGroupsAsync(false);
                return Status.OK;

            default:
                DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `Failed to move group position, controller responded with error ${result?.Status} data: ${result?.Data}`);
                return result?.Status as Status;
        }
    }

    async MoveObjectInGroupPositionAsync(group: IChannelsGroup, channel: IDeviceChannel, newPosition: number): Promise<Status> {
        const objectId = `${channel.GetDevice().Guid}_${channel.Number}`;
        const result = await this._connection?.SendAndWaitForResponseAsync<ApiGroupObject[]>(new MoveObjectInGroupRequest(group.Guid, objectId, newPosition), 15000, false);
        DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! MoveObjectInGroupPositionAsync()`);
        this._configurationService.ExitConfigurationModeAsync();

        switch (result?.Status) {
            case Status.OK:
                await this.GetGroupsAsync(false);
                await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync();
                return Status.OK;

            default:
                DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `Failed to move object in group position, controller responded with error ${result?.Status} data: ${result?.Data}`);
                return result?.Status as Status;
        }
    }

    async AddDeviceChannelToGroupByGuidAsync(channel: IDeviceChannel, groupGuid: string): Promise<Status>;
    async AddDeviceChannelToGroupByGuidAsync(channel: IDeviceChannel, groupGuid: string, skipDataUpdate?: boolean): Promise<Status>;
    async AddDeviceChannelToGroupByGuidAsync(channel: IDeviceChannel, groupGuid: string, skipDataUpdate: boolean = false): Promise<Status> {
        const req = new AddDeviceChannelToGroupRequest();
        (req.Data as AddRemoveDeviceChannelToGroup).Channel = channel.Number;
        let device = (channel as DeviceChannel).GetDevice();

        if (device == null)
            throw new Error("Device is not set on this channel!");

        (req.Data as AddRemoveDeviceChannelToGroup).DeviceGuid = (channel as DeviceChannel).GetDevice()?.Guid as string;
        (req.Data as AddRemoveDeviceChannelToGroup).GroupGuid = groupGuid;

        const result = await this._connection?.SendAndWaitForResponseAsync<any>(req, 15000, false);
        DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! AddDeviceChannelToGroupByGuidAsync()`);
        this._configurationService.ExitConfigurationModeAsync();

        if (result != null && result.Status != null) {
            if (result.Status == Status.OK)
                channel.ChannelGroups.push(groupGuid);

            else
                DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `Failed to add device channel ${channel.Name} to group ${groupGuid}, controller responded with error ${result.Status} data: ${result.Data}`);

            if (!skipDataUpdate) {
                await this.GetGroupsAsync(false);
                await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync();
            }
            return result.Status;
        }

        if (!skipDataUpdate) {
            await this.GetGroupsAsync(false);
            await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync();
        }
        return Status.Error;
    }

    async RemoveDeviceChannelFromGroupByGuidAsync(channel: IDeviceChannel, groupGuid: string): Promise<Status>;
    async RemoveDeviceChannelFromGroupByGuidAsync(channel: IDeviceChannel, groupGuid: string, skipDataUpdate?: boolean): Promise<Status>;
    async RemoveDeviceChannelFromGroupByGuidAsync(channel: IDeviceChannel, groupGuid: string, skipDataUpdate: boolean = false): Promise<Status> {
        const req = new RemoveDeviceChannelToGroupRequest();

        let device = (channel as DeviceChannel).GetDevice();

        if (device == null)
            throw new Error("Device is not set on this channel!");

        (req.Data as AddRemoveDeviceChannelToGroup).DeviceGuid = (channel as DeviceChannel).GetDevice()?.Guid as string;
        (req.Data as AddRemoveDeviceChannelToGroup).Channel = channel.Number;
        (req.Data as AddRemoveDeviceChannelToGroup).GroupGuid = groupGuid;

        const result = await this._connection?.SendAndWaitForResponseAsync<any>(req, 15000, false);
        if (!skipDataUpdate)
            this._configurationService.ExitConfigurationModeAsync();

        if (result != null && result.Status != null) {
            if (result.Status == Status.OK)
                (channel as DeviceChannel).ChannelGroups = channel.ChannelGroups.where(a => a != groupGuid).toArray();
            else
                DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `Failed to remove device channel ${channel.Name} from group ${groupGuid}, controller responded with error ${result.Status} data: ${result.Data}`);

            if (!skipDataUpdate)
                await this.GetGroupsAsync(false);
            await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync();
            return result.Status;
        }

        if (!skipDataUpdate)
            await this.GetGroupsAsync(false);
        await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync();
        return Status.Error;
    }

    async UpsertGroupWithDevicesChannelsAsync(group: IChannelsGroup, deviceChannels: IDeviceChannel[]): Promise<ResponseResult<AddNewGroupErrorCode> | IChannelsGroup> {
        if (this._groups.all(g => g.Guid != group.Guid)) {
            let result = await this.AddNewGroupWithIconAsync(group.Name, group.IconName);

            if (result instanceof ChannelsGroup)
                group = result as ChannelsGroup;
            else if (result instanceof ResponseResult)
                return result;
            else
                return new ResponseResult(AddNewGroupErrorCode.UnknownError, "");
        }

        /// add devices channels to groups that are not already in groups
        for (let channel of deviceChannels.where(a => a.ChannelGroups.all(b => b !== group.Guid))) {
            let status = await this.AddDeviceChannelToGroupAsync(channel, group, true);

            if (status !== Status.OK) {
                DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `Failed to add device channel ${channel.Name} to group ${group.Name}, controller responded with error ${status}`);

                return new ResponseResult(AddNewGroupErrorCode.UnknownError, `Failed to add device channel ${channel.Name} to group ${group.Name}, controller responded with error ${status}`);
            }
        }

        let devices = await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync();

        for (let device of devices) {
            for (let channel of device.Channels.except(deviceChannels, (a, b) => a.GetDevice().Guid == b.GetDevice().Guid && a.Number == b.Number)) {
                for (let gr of channel.ChannelGroups.where(gr => group.Guid == gr)) {
                    let status = await this.RemoveDeviceChannelFromGroupAsync(channel, group, true);

                    if (status !== Status.OK) {
                        DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `Failed to remove device channel ${channel.Name} from group ${group.Name}, controller responded with error ${status}`);
                        DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! UpsertGroupWithDevicesChannelsAsync()`);

                        this._configurationService.ExitConfigurationModeAsync();
                        await this.GetGroupsAsync(false);
                        return new ResponseResult(AddNewGroupErrorCode.UnknownError, `Failed to remove device channel ${channel.Name} from group ${group.Name}, controller responded with error ${status}`);
                    }
                }
            }
        }

        this._configurationService.ExitConfigurationModeAsync();
        await this.GetGroupsAsync(false);
        return group;
    }

    async UpdateGroupWithDevicesChannelsAsync(groupGuid: string, deviceChannels: IDeviceChannel[]): Promise<Status> {
        /// add devices channels to groups that are not already in groups
        for (let channel of deviceChannels.where(a => a.ChannelGroups.all(b => b !== groupGuid))) {
            let status = await this.AddDeviceChannelToGroupByGuidAsync(channel, groupGuid, true);

            if (status !== Status.OK) {
                DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `Failed to add device channel ${channel.Name} to group ${groupGuid}, controller responded with error ${status}`);
                DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! UpdateGroupWithDevicesChannelsAsync()`);
                this._configurationService.ExitConfigurationModeAsync();
                await this.GetGroupsAsync(false);
                return Status.FatalError;
            }
        }

        let devices = await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync();

        for (let device of devices) {
            for (let channel of device.Channels.except(deviceChannels, (a, b) => a.GetDevice().Guid == b.GetDevice().Guid && a.Number == b.Number)) {
                for (let gr of channel.ChannelGroups.where(gr => groupGuid == gr)) {
                    let status = await this.RemoveDeviceChannelFromGroupByGuidAsync(channel, groupGuid, true);

                    if (status !== Status.OK) {
                        DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `Failed to remove device channel ${channel.Name} from group ${groupGuid}, controller responded with error ${status}`);
                        DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! UpdateGroupWithDevicesChannelsAsync()`);
                        this._configurationService.ExitConfigurationModeAsync();
                        await this.GetGroupsAsync(false);
                        return Status.FatalError;
                    }
                }
            }
        }
        this._configurationService.ExitConfigurationModeAsync();
        await this.GetGroupsAsync(false);
        return Status.OK;
    }

    async ChangeGroupNameByGuidAsync(groupGuid: string, name: string): Promise<ResponseResult<AddNewGroupErrorCode>> {
        let req = new AddUpdateChannelsGroupsRequest();
        let gr = new ApiGroupObject();
        let tmp = this._groups.first(a => a.Guid === groupGuid);
        gr.Guid = groupGuid;
        gr.Name = name;
        gr.IconName = tmp.IconName;
        req.Data?.push(gr);
        const result = await this._connection?.SendAndWaitForResponseAsync<any>(req, 15000, false);
        this._configurationService.ExitConfigurationModeAsync();
        this._groups = await this.GetGroupsAsync(false);

        if (result != null && result.Status != null) {
            switch (result.Status) {
                case Status.OK:
                    return new ResponseResult(AddNewGroupErrorCode.Success, "");

                case Status.WrongData:
                    switch (result.Data) {
                        case "NameToShort":
                            return new ResponseResult(AddNewGroupErrorCode.NameToShort, "At least 3 characters required");

                        case "NameAlreadyExists":
                            return new ResponseResult(AddNewGroupErrorCode.NameAlreadyExists, "Group with this name already exists");

                        default:
                            return new ResponseResult(AddNewGroupErrorCode.OtherError, `Response error: ${result.Status} data: ${result.Data}`);
                    }

                default:
                    return new ResponseResult(AddNewGroupErrorCode.OtherError, `Response error: ${result.Status} data: ${result.Data}`);
            }
        }

        return new ResponseResult(AddNewGroupErrorCode.UnknownError, "");
    }

    ChangeGroupNameAndIconAsync(group: IChannelsGroup, name: string, iconName: string): Promise<ResponseResult<AddNewGroupErrorCode>> {
        return this.ChangeGroupNameAndIconByGuidAsync(group.Guid, name, iconName);
    }

    async ChangeGroupNameAndIconByGuidAsync(groupGuid: string, name: string, iconName: string): Promise<ResponseResult<AddNewGroupErrorCode>> {
        let req = new AddUpdateChannelsGroupsRequest();
        let gr = new ApiGroupObject();
        let tmp = this._groups.first(a => a.Guid === groupGuid);
        gr.Guid = groupGuid;
        gr.Name = name;
        gr.IconName = iconName;
        gr.ObjectsOrder = tmp.ObjectsOrder;
        req.Data?.push(gr);
        const result = await this._connection?.SendAndWaitForResponseAsync<any>(req, 15000, false);
        this._configurationService.ExitConfigurationModeAsync();
        this._groups = await this.GetGroupsAsync(false);

        if (result != null && result.Status != null) {
            switch (result.Status) {
                case Status.OK:
                    return new ResponseResult(AddNewGroupErrorCode.Success, "");

                case Status.WrongData:
                    switch (result.Data) {
                        case "NameToShort":
                            return new ResponseResult(AddNewGroupErrorCode.NameToShort, "At least 3 characters required");

                        case "NameAlreadyExists":
                            return new ResponseResult(AddNewGroupErrorCode.NameAlreadyExists, "Group with this name already exists");

                        default:
                            return new ResponseResult(AddNewGroupErrorCode.OtherError, `Response error: ${result.Status} data: ${result.Data}`);
                    }

                default:
                    return new ResponseResult(AddNewGroupErrorCode.OtherError, `Response error: ${result.Status} data: ${result.Data}`);
            }
        }

        return new ResponseResult(AddNewGroupErrorCode.UnknownError, "");
    }

    async ChangeGroupIconNameByGuidAsync(groupGuid: string, iconName: string): Promise<ResponseResult<AddNewGroupErrorCode>> {
        let req = new AddUpdateChannelsGroupsRequest();
        let gr = new ApiGroupObject();
        let tmp = this._groups.first(a => a.Guid === groupGuid);
        gr.Guid = groupGuid;
        gr.Name = tmp.Name;
        gr.IconName = iconName;
        req.Data?.push(gr);
        const result = await this._connection?.SendAndWaitForResponseAsync<any>(req, 15000, false);
        this._configurationService.ExitConfigurationModeAsync();
        this._groups = await this.GetGroupsAsync(false);

        if (result != null && result.Status != null) {
            switch (result.Status) {
                case Status.OK:
                    return new ResponseResult(AddNewGroupErrorCode.Success, "");

                case Status.WrongData:
                    switch (result.Data) {
                        case "NameToShort":
                            return new ResponseResult(AddNewGroupErrorCode.NameToShort, "At least 3 characters required");

                        case "NameAlreadyExists":
                            return new ResponseResult(AddNewGroupErrorCode.NameAlreadyExists, "Group with this name already exists");

                        default:
                            return new ResponseResult(AddNewGroupErrorCode.OtherError, `Response error: ${result.Status} data: ${result.Data}`);
                    }

                default:
                    return new ResponseResult(AddNewGroupErrorCode.OtherError, `Response error: ${result.Status} data: ${result.Data}`);
            }
        }

        return new ResponseResult(AddNewGroupErrorCode.UnknownError, "");
    }

    GetServiceName(): string {
        return ChannelsGroupsService.ServiceName;
    }

    GetGroupByGuid(guid: string): IChannelsGroup | null {
        return this._groups.first(a => a.Guid === guid);
    }

    ChangeGroupNameAsync(group: IChannelsGroup, name: string): Promise<ResponseResult<AddNewGroupErrorCode>> {
        return this.ChangeGroupNameByGuidAsync(group.Guid, name);
    }

    ChangeGroupIconNameAsync(group: IChannelsGroup, iconName: string): Promise<ResponseResult<AddNewGroupErrorCode>> {
        return this.ChangeGroupIconNameByGuidAsync(group.Guid, iconName);
    }

    RemoveGroupAsync(group: IChannelsGroup): Promise<Status> {
        return this.RemoveGroupByGuidAsync(group.Guid);
    }

    async RemoveGroupByGuidAsync(groupGuid: string): Promise<Status> {
        let req = new RemoveGroupRequest();
        req.Data = groupGuid;
        const result = await this._connection?.SendAndWaitForResponseAsync<any>(req, 15000, false);
        this._configurationService.ExitConfigurationModeAsync();
        await this.GetGroupsAsync(false);

        if (result != null && result.Status != null)
            return result?.Status;

        return Status.Error;
    }

    async GetGroupsAsync(useCache: boolean = true): Promise<IChannelsGroup[]> {
        const result = await this._connection?.SendAndWaitForResponseAsync<ApiGroupObject[]>(new GetGroupsRequest(), 15000, useCache);
        let groups: ChannelsGroup[] = [];

        switch (result?.Status) {
            case Status.OK:
                let i = 0;
                let log = Api.Get<ILoggerService>(LoggerService.ServiceName);
                let devs = await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync();

                result.Data?.forEach(group => {
                    i++;
                    let gr = new ChannelsGroup();
                    gr.Guid = group.Guid;
                    gr.Name = group.Name;
                    gr.IconName = group.IconName;
                    gr.ObjectsOrder = group.ObjectsOrder;
                    gr.OrderPosition = group.OrderPosition;

                    if (gr.OrderPosition == undefined)
                        gr.OrderPosition = i;

                    if (gr.ObjectsOrder == undefined) {
                        this._objectsGroupingSupported = false;
                        gr.ObjectsOrder = {};
                        let tmp = devs.selectMany(a => a.Channels.where(b => b.ChannelGroups.any(c => c == gr.Guid)));

                        let position = 0;

                        for (let channel of tmp) {
                            position++;
                            gr.ObjectsOrder[position] = channel.ChannelId;
                        }
                    }
                    else
                        this._objectsGroupingSupported = true;

                    if (!useCache)
                        log.Info(ChannelsGroupsService.ServiceName, `Got group: ${gr.Name} ${gr.Guid} order: ${gr.OrderPosition} objects order: ${JSON.stringify(gr.ObjectsOrder)}`);

                    groups.push(gr);
                });

                this._groups = groups;
                return groups;

            default:
                throw new Error(`Failed to get groups, API responded with status ${result?.Status} data: ${result?.Data}`);
        }
    }

    AddDeviceChannelToGroupAsync(channel: IDeviceChannel, group: IChannelsGroup): Promise<Status>;
    AddDeviceChannelToGroupAsync(channel: IDeviceChannel, group: IChannelsGroup, skipDataUpdate?: boolean): Promise<Status>;
    AddDeviceChannelToGroupAsync(channel: IDeviceChannel, group: IChannelsGroup, skipDataUpdate: boolean = false): Promise<Status> {
        return this.AddDeviceChannelToGroupByGuidAsync(channel, group.Guid, skipDataUpdate);
    }

    RemoveDeviceChannelFromGroupAsync(channel: IDeviceChannel, group: IChannelsGroup): Promise<Status>;
    RemoveDeviceChannelFromGroupAsync(channel: IDeviceChannel, group: IChannelsGroup, skipDataUpdate?: boolean): Promise<Status>;
    RemoveDeviceChannelFromGroupAsync(channel: IDeviceChannel, group: IChannelsGroup, skipDataUpdate: boolean = false): Promise<Status> {
        return this.RemoveDeviceChannelFromGroupByGuidAsync(channel, group.Guid, skipDataUpdate);
    }

    async AddNewGroupAsync(name: string): Promise<IChannelsGroup | ResponseResult<AddNewGroupErrorCode>> {
        let req = new AddUpdateChannelsGroupsRequest();
        let gr = new ApiGroupObject();
        gr.Name = name;
        req.Data?.push(gr);
        const result = await this._connection?.SendAndWaitForResponseAsync<any>(req, 15000, false);
        this._configurationService.ExitConfigurationModeAsync();
        await this.GetGroupsAsync(false);

        if (result != null && result.Status != null) {
            switch (result.Status) {
                case Status.OK:
                    return this._groups.first(a => a.Name === name);

                case Status.WrongData:
                    DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `Failed to add new group, controller responded with error ${result.Status} data: ${result.Data}`);

                    switch (result.Data) {
                        case "NameToShort":
                            return new ResponseResult(AddNewGroupErrorCode.NameToShort, "At least 3 characters required");

                        case "NameAlreadyExists":
                            return new ResponseResult(AddNewGroupErrorCode.NameAlreadyExists, "Group with this name already exists");

                        default:
                            return new ResponseResult(AddNewGroupErrorCode.OtherError, `Response error: ${result.Status} data: ${result.Data}`);
                    }

                default:
                    return new ResponseResult(AddNewGroupErrorCode.OtherError, `Response error: ${result.Status} data: ${result.Data}`);
            }
        }

        return new ResponseResult(AddNewGroupErrorCode.UnknownError, "");
    }

    async AddNewGroupWithIconAsync(name: string, iconName: string): Promise<IChannelsGroup | ResponseResult<AddNewGroupErrorCode>> {
        let req = new AddUpdateChannelsGroupsRequest();
        let gr = new ApiGroupObject();
        gr.Name = name;
        gr.IconName = iconName;
        req.Data?.push(gr);
        const result = await this._connection?.SendAndWaitForResponseAsync<any>(req, 15000, false);
        this._configurationService.ExitConfigurationModeAsync();
        await this.GetGroupsAsync(false);

        if (result != null && result.Status != null) {
            switch (result.Status) {
                case Status.OK:
                    return this._groups.first(a => a.Name === name);

                case Status.WrongData:
                    DependencyContainer.Log?.Error(ChannelsGroupsService.ServiceName, `Failed to add new group, controller responded with error ${result.Status} data: ${result.Data}`);

                    switch (result.Data) {
                        case "NameToShort":
                            return new ResponseResult(AddNewGroupErrorCode.NameToShort, "At least 3 characters required");

                        case "NameAlreadyExists":
                            return new ResponseResult(AddNewGroupErrorCode.NameAlreadyExists, "Group with this name already exists");

                        default:
                            return new ResponseResult(AddNewGroupErrorCode.OtherError, `Response error: ${result.Status} data: ${result.Data}`);
                    }

                default:
                    return new ResponseResult(AddNewGroupErrorCode.OtherError, `Response error: ${result.Status} data: ${result.Data}`);
            }
        }

        return new ResponseResult(AddNewGroupErrorCode.UnknownError, "");
    }
}

class MoveGroupRequest extends DataFrame<MoveGroup> {
    constructor(groupGuid: string, newPosition: number) {
        super();
        this.Resource = "/devices/groups/group/move";
        this.Method = Method.Post;
        this.Data = new MoveGroup();
        this.Data.GroupGuid = groupGuid;
        this.Data.NewPosition = newPosition;
    }
}

class ChangeGroupsOrderRequest extends DataFrame<string[]> {
    constructor(orderedObjectsGuid: string[]) {
        super();
        this.Resource = "/devices/groups/move";
        this.Method = Method.Post;
        this.Data = orderedObjectsGuid;
    }
}

class SetObjectsInGroupAndOrderRequest extends DataFrame<ObjectsAndOrderInGroup> {
    constructor(group: IChannelsGroup, objectsIds: string[]) {
        super();
        this.Resource = "/devices/groups/group/objects/set";
        this.Method = Method.Post;
        this.Data = new ObjectsAndOrderInGroup();
        this.Data.GroupGuid = group.Guid;
        this.Data.ObjectsIds = objectsIds;
    }
}

class ObjectsAndOrderInGroup {
    public GroupGuid: string = "";
    public ObjectsIds: string[] = [];
}

class MoveGroup {
    public GroupGuid: string = "";
    public NewPosition: number = 0;
}

class MoveObjectInGroupRequest extends DataFrame<MoveObjectInGroup> {
    constructor(groupGuid: string, objectId: string, newPosition: number) {
        super();
        this.Resource = "/devices/groups/group/object/move";
        this.Method = Method.Post;
        this.Data = new MoveObjectInGroup();
        this.Data.GroupGuid = groupGuid;
        this.Data.NewPosition = newPosition;
        this.Data.ObjectId = objectId;
    }
}

class MoveObjectsInGroupRequest extends DataFrame<MoveObjectsInGroup> {
    constructor(groupGuid: string, orderedObjectsIds: string[]) {
        super();
        this.Resource = "/devices/groups/group/objects/move";
        this.Method = Method.Post;
        this.Data = new MoveObjectsInGroup();
        this.Data.GroupGuid = groupGuid;
        this.Data.OrderedObjectsIds = orderedObjectsIds;
    }
}

class MoveObjectInGroup {
    public GroupGuid: string = "";
    public ObjectId: string = "";
    public NewPosition: number = 0;
}

class MoveObjectsInGroup {
    public GroupGuid: string = "";
    public OrderedObjectsIds: string[] = [];
}

class RemoveGroupRequest extends DataFrame<string> {
    constructor() {
        super();
        this.Resource = "/devices/groups/group";
        this.Method = Method.Delete;
    }
}

class GetGroupsRequest extends DataFrame<string> {
    constructor() {
        super();
        this.Resource = "/devices/groups/";
        this.Method = Method.Get;
    }
}

class AddUpdateChannelsGroupsRequest extends DataFrame<ApiGroupObject[]> {
    constructor() {
        super();
        this.Resource = "/devices/groups/group";
        this.Method = Method.Put;
        this.Data = [];
    }
}

class AddDeviceChannelToGroupRequest extends DataFrame<AddRemoveDeviceChannelToGroup> {
    constructor() {
        super();
        this.Resource = "/devices/device/channel/groups/group/";
        this.Method = Method.Put;
        this.Data = new AddRemoveDeviceChannelToGroup();
    }
}

class RemoveDeviceChannelToGroupRequest extends DataFrame<AddRemoveDeviceChannelToGroup> {
    constructor() {
        super();
        this.Resource = "/devices/device/channel/groups/group/";
        this.Method = Method.Delete;
        this.Data = new AddRemoveDeviceChannelToGroup();
    }
}

class ApiGroupObject {
    Id: number = 0;
    Guid: string = "";
    Name: string = "";
    IconName: string = "";
    OrderPosition: number = 0;
    ObjectsOrder: Record<number, string> = {};
}

class AddRemoveDeviceChannelToGroup {
    GroupGuid: string = "";
    DeviceGuid: string = "";
    Channel: number = 0;
}
